import { Component, ChangeDetectionStrategy, Input, OnDestroy, ViewChild, ChangeDetectorRef, OnChanges, Output, EventEmitter } from '@angular/core';
import { ReportDefinitionColumnOptions } from '@key-telematics/fleet-api-client';
import { KuiTreeSelectComponent, KuiTreeSelectNode } from 'app/key-ui/tree-select/tree-select.component';
import { TreeNode } from '@ali-hm/angular-tree-component';
import { KuiDropdownComponent } from 'app/key-ui/dropdown/dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../modal';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';import { ReportColumnTreeService } from '../services/report-column-tree.service';


@Component({
    selector: 'key-reporting-builder-columns',
    templateUrl: './columns.component.html',
    styleUrls: ['./columns.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReportingBuilderColumnsComponent implements OnDestroy, OnChanges {

    @Input() columns: ReportDefinitionColumnOptions[];
    @Input() availableColumns: ReportDefinitionColumnOptions[];
    @Input() autoColumns: string[];
    @Output() onUpdateColumns = new EventEmitter<ReportDefinitionColumnOptions[]>();

    @ViewChild(KuiDropdownComponent, { static: true }) filterDropdown: KuiDropdownComponent;

    treeNodes: KuiTreeSelectNode[];

    constructor(
        private i18n: TranslateService,
        private ref: ChangeDetectorRef,
        private modal: ModalService,
        private treeService: ReportColumnTreeService,
    ) {
    }

    ngOnDestroy() {
    }

    ngOnChanges() {
        this.buildTree().then(nodes => {
            this.treeNodes = nodes;
            this.ref.markForCheck();
        });
    }

    drop(event: CdkDragDrop<any[]>) {
        moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
    }

    sortColumns(columns: ReportDefinitionColumnOptions[]) {
        this.columns = columns;
        this.onUpdateColumns.emit(columns);
    }

    removeColumn(column: ReportDefinitionColumnOptions) {
        const idx = this.columns.indexOf(column);
        if (idx !== -1) {
            this.columns.splice(idx, 1);
            this.ref.markForCheck();
        }
    }

    resetColumns() {
        this.columns.splice(0, this.columns.length);
        this.availableColumns.filter(x => x.def).forEach(column => {
            this.columns.push(column);
        });
    }

    async nodeSelected(event: { eventName: string, node: TreeNode }, dropdown: KuiDropdownComponent, tree: KuiTreeSelectComponent) {
        if (event.eventName === 'activate') {
            const { data, id } = event.node.data;
            if (data && data.column) {
                dropdown.toggle();
                tree.clearSelection();

                if (id === 'custom') {
                    // let user set an expression before adding custom column
                    const update = await this.updateColumnExpression({
                        ...data.column,
                        // give each new custom column a unique id like custom_1, custom_2 etc.
                        id: id + '_' + (this.columns.filter(x => x.id.includes('custom_')).length + 1),
                    });
                    if (update) {
                        this.columns.push(update);
                    }
                } else {
                    // only add the column if it hasn't already been added before
                    if (!this.columns.find(x => x.id === data.column.id)) {
                        this.columns.push(data.column);
                    }
                }

                this.ref.markForCheck();
            }
        }
    }

    async buildTree(): Promise<KuiTreeSelectNode[]> {
        const autoColumns = this.autoColumns || [];
        const treeAutoColumns = autoColumns.filter(c => c !== 'custom');
        const nodes = await this.treeService.buildTree({
            availableColumns: this.availableColumns,
            autoColumns: treeAutoColumns,
        });
        const result = nodes as KuiTreeSelectNode[];
        if (autoColumns.includes('custom')) {
            result.push(this.getCustomNode());
        }
        return result;
    }

    private getCustomNode(): KuiTreeSelectNode {
        const name = this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.CUSTOM');
        return {
            id: 'custom',
            name,
            children: null,
            data: {
                column: {
                    id: 'custom',
                    title: name,
                    width: 100,
                    align: 'Left',
                    def: false,
                    required: false,
                },
            },
        } as KuiTreeSelectNode;
    }

    async updateColumnExpression(column: ReportDefinitionColumnOptions): Promise<ReportDefinitionColumnOptions> {
        const translate = key => this.i18n.instant(`REPORTING.BUILDER.COLUMNS.MODAL.CUSTOM.${key}`);
        const res = await this.modal.form({
            groups: [{
                name: translate('TITLE'),
                fields: [{ type: 'memo', id: 'expression', title: translate('FIELD_EXPRESSION.TITLE'), description: translate('FIELD_EXPRESSION.DESC'), required: true }],
            }],
        }, { expression: column.expression });

        if (!res) { return null; }
        column.expression = res.expression;
        return column;
    }
}
